import { inject, Injectable } from '@angular/core';
import { filter, firstValueFrom } from 'rxjs';
import { ISystemMessage } from '@newroom-connect/library/interfaces';
import { TranslationService } from '@newroom-connect/library/services';
import { ModalSize } from '@newroom-connect/library/enums';

import { ModalService } from '../../modal/modal.component';
import { UnsavedChangesAlertComponent } from '../unsaved-changes-alert.component';

@Injectable({
  providedIn: 'root'
})
export class UnsavedChangesService {
  private readonly translationService = inject(TranslationService);
  private readonly modalService = inject(ModalService);
  private pendingConfirmation: Promise<boolean> | null = null;

  /**
   *
   * @param message
   * @returns
   */
  public async confirmUnsavedChanges(message: ISystemMessage): Promise<boolean> {
    // If there's already a pending confirmation, return it instead of creating a new modal
    if (this.pendingConfirmation) {
      return this.pendingConfirmation;
    }

    const modalId = `unsaved-changes-${Date.now()}`;

    // Create and store the pending confirmation promise
    this.pendingConfirmation = this.showConfirmationModal(modalId, message);

    try {
      // Wait for the modal to close and get the result.
      const result = await this.pendingConfirmation;

      return result;
    } finally {
      // Clear the pending confirmation when done
      this.pendingConfirmation = null;
    }
  }

  private async showConfirmationModal(modalId: string, message: ISystemMessage): Promise<boolean> {
    // Open the modal
    this.modalService.open({
      component: UnsavedChangesAlertComponent,
      inputs: {
        id: modalId,
        size: ModalSize.SMALL,
        alertMessage: message
      }
    });

    // Wait for the modal to close and get the result
    const result = await firstValueFrom(
      this.modalService.modalClosed$.pipe(
        filter(event => event.id === modalId)
      )
    );

    return !!result && !!result.result;
  }
}
